import type { ZudokuConfig } from "zudoku";
import AiChatDrawer from "./src/AiChat/AiChatDrawer";
import AiScriptPlugin from "./src/AiChat/AiScriptPlugin";

const config: ZudokuConfig = {
  navigation: [
    {
      type: "category",
      label: "Documentation",
      items: [
        {
          type: "category",
          label: "Overview",
          icon: "book",
          items: ["docs/introduction", "docs/banks"]
        },
        {
          type: "category",
          label: "Guides",
          icon: "compass",
          items: ["docs/getstarted", "docs/api", "docs/get_access_token",
            {
              type: "category",
              label: "Core APIs",
              icon: "layers",
              items: ["docs/kyc", "docs/list_banks", "docs/consent_and_ais", "docs/quickstart_pis", "docs/authorisations"]
            },
            {
              type: "category",
              label: "Alternative APIs",
              icon: "puzzle",
              items: ["docs/iso_payments", "docs/fx_connect"]
            },
            {
              type: "category",
              label: "Premium APIs",
              icon: "sparkles",
              items: ["docs/bankgiro_lookup", "docs/payouts", "docs/vop", "docs/sepa_direct_debit"]
            }
          ]
        },
        {
          type: "category",
          label: "Other Information",
          icon: "info",
          items: ["docs/sandbox", "docs/glossary", "docs/enums", "docs/error_messages"]
        },
        {
          type: "category",
          label: "Changelog",
          icon: "rocket",
          items: [
            "docs/changelog/2025-04", 
            "docs/changelog/2025-05", 
            "docs/changelog/2025-06", 
            "docs/changelog/2025-07", 
            "docs/changelog/2025-08", 
            "docs/changelog/2025-09", 
            "docs/changelog/2025-11", 
            "docs/changelog/2026-01", 
            "docs/changelog/2026-02", 
            "docs/changelog/2026-03", 
            "docs/changelog/2026-04", 
            "docs/changelog/2026-05", 
            "docs/changelog/2026-06", 
            "docs/changelog/2026-07", 
            "docs/changelog/2026-08", 
            "docs/changelog/2026-09"
          ]
        },
        {
          type: "category",
          label: "Downloads",
          icon: "download",
          items: [
            {
              type: "link",
              label: "OpenAPI Reference",
              to: "https://docs.openpayments.io/openpayments_api.yaml",
            },
            {
              type: "link",
              label: "Postman Collection",
              to: "https://docs.openpayments.io/obp.postman_collection.json"
            },
            {
              type: "link",
              label: "Postman Sandbox Environment",
              to: "https://docs.openpayments.io/sandbox.postman_environment.json"
            },
            {
              type: "link",
              label: "Postman Production Environment",
              to: "https://docs.openpayments.io/production.postman_environment.json"
            }
          ]
        },
        {
          type: "category",
          label: "Support",
          icon: "mail-question",
          items: ["docs/support",
            {
              type: "link",
              label: "Status",
              to: "https://statuspage.incident.io/openpayments"
            },
            {
              type: "link",
              label: "Terms & Conditions",
              to: "https://docs.openpayments.io/legal/Terms%20%26%20Conditions.pdf"
            },
            {
              type: "link",
              label: "Privacy Policy",
              to: "https://docs.openpayments.io/legal/PrivacyPolicy2023.pdf"
            },
            {
              type: "link",
              label: "Main Website",
              to: "https://openpayments.io"
            }
          ]
        },
      ]
    },
    {
      type: "link",
      to: "/api",
      label: "API Reference",
    },
  ],
  apis: [
    {
      path: "/api",
      input: "./public/openpayments_api.yaml",
      type: "file"
    }
  ],
  metadata: {
    title: "Open Payments Documentation",
    favicon: "/favicon.png"
  },
  redirects: [{ from: "/", to: "/docs/introduction" }],
  docs: {
    files: "/pages/**/*.{md,mdx}",
  },
  site: {
    showPoweredBy: false,
    title: "",
    logo: {
      src: {
        light: "OP-logotype-regular.png",
        dark: "OP-logotype-regular-dark.png"
      }
    }
  },
  theme: {
    fonts: {
      sans: {
        fontFamily: "Scto Grotesk A, Open Sans, sans-serif",
        url: "/fonts.css"
      }
    },
    light: {
      background: "0 0% 100%",
      foreground: "0 0% 3.9%",
      card: "0 0% 100%",
      cardForeground: "0 0% 3.9%",
      popover: "0 0% 100%",
      popoverForeground: "0 0% 3.9%",
      primary: "0 0% 9%",
      primaryForeground: "0 0% 98%",
      secondary: "0 0% 96.1%",
      secondaryForeground: "0 0% 9%",
      muted: "0 0% 96.1%",
      mutedForeground: "0 0% 45.1%",
      accent: "0 0% 96.1%",
      accentForeground: "0 0% 9%",
      destructive: "0 84.2% 60.2%",
      destructiveForeground: "0 0% 98%",
      border: "0 0% 89.8%",
      input: "0 0% 89.8%",
      ring: "0 0% 3.9%",
      radius: "0.8rem"
    },
    dark: {
      background: "0 0% 3.9%",
      foreground: "0 0% 98%",
      card: "0 0% 3.9%",
      cardForeground: "0 0% 98%",
      popover: "0 0% 3.9%",
      popoverForeground: "0 0% 98%",
      primary: "0 0% 98%",
      primaryForeground: "0 0% 9%",
      secondary: "0 0% 14.9%",
      secondaryForeground: "0 0% 98%",
      muted: "0 0% 14.9%",
      mutedForeground: "0 0% 63.9%",
      accent: "0 0% 14.9%",
      accentForeground: "0 0% 98%",
      destructive: "0 62.8% 30.6%",
      destructiveForeground: "0 0% 98%",
      border: "0 0% 14.9%",
      input: "0 0% 14.9%",
      ring: "0 0% 83.1%",
      radius: "0.5rem"
    },
    customCss: `
      .prose table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.875rem;
        margin: 1.5rem 0;
        border-radius: var(--radius);
        overflow: hidden;
        display: table;
      }

      .prose thead {
        background-color: hsl(var(--muted));
      }

      .prose thead th {
        padding: 0.75rem 1rem;
        text-align: left;
        font-weight: 700;
        font-size: 0.8125rem;
        letter-spacing: 0.03em;
        color: hsl(var(--foreground));
        border: 1px solid hsl(var(--border)) !important;
        white-space: nowrap;
      }

      .prose tbody tr:nth-child(odd) {
        background-color: hsl(var(--background));
      }

      .prose tbody tr:nth-child(even) {
        background-color: hsl(var(--muted) / 0.4);
      }

      .prose tbody tr:hover {
        background-color: hsl(var(--muted) / 0.7);
      }

      .prose tbody td {
        padding: 0.625rem 1rem;
        vertical-align: top;
        line-height: 1.5;
        border: 1px solid hsl(var(--border)) !important;
      }

      .prose tbody td:first-child {
        font-family: ui-monospace, Menlo, Consolas, monospace;
        font-size: 0.8125rem;
        white-space: nowrap;
      }

      .prose table:has(thead th:nth-child(3)) tbody td:nth-child(2) {
        font-size: 0.875rem;
        color: hsl(var(--muted-foreground));
        white-space: nowrap;
      }
    `,
  },
  slots: {
    "top-navigation-side": <AiChatDrawer />
  },
  plugins: [AiScriptPlugin],
  port: 3000
};

export default config;
